// Hitgroup panel data: region shares, peer shares and hit efficiency, all
// derived from the per-player stat map the match payload already carries.
import { t } from '../i18n';

// Labels live under shared.hitgroups.<key> and are resolved in buildHitgroupData.
export const HITGROUP_REGIONS = [
  { key: 'head', hits: 'hitgroup_head_hits', damage: 'hitgroup_head_damage' },
  { key: 'chest', hits: 'hitgroup_chest_hits', damage: 'hitgroup_chest_damage' },
  { key: 'stomach', hits: 'hitgroup_stomach_hits', damage: 'hitgroup_stomach_damage' },
  { key: 'left_arm', hits: 'hitgroup_left_arm_hits', damage: 'hitgroup_left_arm_damage' },
  { key: 'right_arm', hits: 'hitgroup_right_arm_hits', damage: 'hitgroup_right_arm_damage' },
  { key: 'left_leg', hits: 'hitgroup_left_leg_hits', damage: 'hitgroup_left_leg_damage' },
  { key: 'right_leg', hits: 'hitgroup_right_leg_hits', damage: 'hitgroup_right_leg_damage' },
];

// Fallback only: the served hitgroup_efficiency recipe is the authority, and
// buildHitgroupData prefers its coefficients when definitions are loaded.
export const HITGROUP_MAX_MULTIPLIER = 4.0;

export const HITGROUP_MULTIPLIERS = {
  head: 4.0,
  chest: 1.0,
  stomach: 1.25,
  left_arm: 1.0,
  right_arm: 1.0,
  left_leg: 0.75,
  right_leg: 0.75,
};

// Head above bracket is aim; everything else above bracket is drift.
const BETTER_ABOVE = new Set(['head']);
const NEUTRAL = new Set(['chest']);

function statNumber(entry, field) {
  const raw = entry?.[field];
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function multipliersFrom(definitions) {
  const recipe = definitions?.hitgroup_efficiency;
  const divisor = Number(recipe?.divisor) > 0 ? Number(recipe.divisor) : HITGROUP_MAX_MULTIPLIER;
  const inputs = recipe?.inputs;
  if (!Array.isArray(inputs) || inputs.length === 0) return { byRegion: HITGROUP_MULTIPLIERS, divisor };
  const byRegion = {};
  HITGROUP_REGIONS.forEach((region) => {
    const input = inputs.find((candidate) => candidate.key === region.hits);
    byRegion[region.key] = input?.coefficient ?? HITGROUP_MULTIPLIERS[region.key];
  });
  return { byRegion, divisor };
}

// Share of the maximum: 100% = every bullet in the head.
function efficiencyOf(hitsByRegion, multipliers, divisor) {
  let points = 0;
  let hits = 0;
  Object.entries(hitsByRegion).forEach(([key, count]) => {
    if (count == null) return;
    points += count * multipliers[key];
    hits += count;
  });
  return hits === 0 ? null : (points / hits / divisor) * 100;
}

// Shapes the panel's model: per-region hits/damage/shares plus player and
// bracket efficiency. `values` is the match payload's per-player stat map.
export function buildHitgroupData(values, definitions = null) {
  if (!values) return null;

  const { byRegion: multipliers, divisor } = multipliersFrom(definitions);
  const regions = HITGROUP_REGIONS.map((region) => ({
    ...region,
    label: t(`shared.hitgroups.${region.key}`),
    hitCount: statNumber(values[region.hits], 'value'),
    damage: statNumber(values[region.damage], 'value'),
    peerHits: statNumber(values[region.hits], 'avg'),
  }));

  // Neck folds into the head everywhere: same 4x multiplier, same aim signal.
  const head = regions[0];
  const addNeck = (base, extra) => (base == null && extra == null ? null : (base ?? 0) + (extra ?? 0));
  head.hitCount = addNeck(head.hitCount, statNumber(values.hitgroup_neck_hits, 'value'));
  head.damage = addNeck(head.damage, statNumber(values.hitgroup_neck_damage, 'value'));
  head.peerHits = addNeck(head.peerHits, statNumber(values.hitgroup_neck_hits, 'avg'));

  const totalHits = regions.reduce((sum, r) => sum + (r.hitCount ?? 0), 0);
  const totalDamage = regions.reduce((sum, r) => sum + (r.damage ?? 0), 0);
  const totalPeerHits = regions.reduce((sum, r) => sum + (r.peerHits ?? 0), 0);
  if (totalHits === 0) return null;

  const playerHits = {};
  const peerHits = {};
  regions.forEach((r) => {
    playerHits[r.key] = r.hitCount;
    peerHits[r.key] = r.peerHits;
    r.hitShare = totalHits === 0 ? null : ((r.hitCount ?? 0) / totalHits) * 100;
    r.damageShare = totalDamage === 0 ? null : ((r.damage ?? 0) / totalDamage) * 100;
    r.peerShare = totalPeerHits === 0 ? null : ((r.peerHits ?? 0) / totalPeerHits) * 100;
    r.direction = BETTER_ABOVE.has(r.key) ? 'above' : NEUTRAL.has(r.key) ? 'none' : 'below';
  });

  // Prefer the stored stat; recompute only when the payload predates it.
  const stored = statNumber(values.hitgroup_efficiency, 'value');
  const efficiency = stored != null && stored > 0 ? stored * 100 : efficiencyOf(playerHits, multipliers, divisor);
  const peerEfficiency = totalPeerHits === 0 ? null : efficiencyOf(peerHits, multipliers, divisor);

  return {
    regions, totalHits, totalDamage, efficiency, peerEfficiency, multipliers, divisor,
  };
}

// Delta of a region's hit share vs the bracket, colored by its direction.
export function regionDelta(region) {
  if (region.hitShare == null || region.peerShare == null) return null;
  const diff = region.hitShare - region.peerShare;
  let tone = 'flat';
  if (region.direction !== 'none' && Math.abs(diff) >= 1) {
    tone = (region.direction === 'above') === (diff > 0) ? 'good' : 'bad';
  }
  return { diff, tone };
}
